import React, { createContext, useReducer, Dispatch } from "react";
import { CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";

enum CustomerActionType {
  CustomerChanged,
  SiteChanged,
}

export type CustomerAction = {
  type: CustomerActionType;
  customer?: CustomerDTO | undefined;
  site?: SiteDTO | undefined;
};

export type CustomerState = {
  customer: CustomerDTO | undefined;
  site: SiteDTO | undefined;
};

type CustomerDispatch = Dispatch<CustomerAction>;

const CustomerStateContext = createContext<CustomerState | undefined>(undefined);
const CustomerDispatchContext = createContext<CustomerDispatch | undefined>(undefined);

function createInitialState(): CustomerState {
  return { 
    customer: undefined,
    site: undefined
  };
}

function customerReducer(state: CustomerState, action: CustomerAction): CustomerState {
  switch (action.type) {
    case CustomerActionType.CustomerChanged: {
      return {
        ...state,
        customer: action.customer,
        site: undefined,
      };
    }
    case CustomerActionType.SiteChanged: {
      return {
        ...state,
        site: action.site,
      };
    }
    default: {
      return state;
    }
  }
}

interface CustomerProviderProps {
  children: React.ReactNode;
  initialState?: CustomerState;
}

function CustomerProvider({ children, initialState }: CustomerProviderProps) {
  initialState =
    initialState !== null ? { ...createInitialState(), ...initialState } : createInitialState();
  const [customerState, customerDispatch] = useReducer(customerReducer, initialState);

  return (
    <CustomerStateContext.Provider value={customerState}>
      <CustomerDispatchContext.Provider value={customerDispatch}>
        {children}
      </CustomerDispatchContext.Provider>
    </CustomerStateContext.Provider>
  );
}

function useCustomerState() {
  const context = React.useContext(CustomerStateContext);

  if (context === undefined) {
    throw new Error(`useCustomerState must be used within a CustomerProvider`);
  }
  return context;
}

function useCustomerDispatch() {
  const context = React.useContext(CustomerDispatchContext);

  if (context === undefined) {
    throw new Error(`useCustomerDispatch must be used within a CustomerProvider`);
  }

  return context;
}

export { CustomerProvider, CustomerActionType, useCustomerState, useCustomerDispatch };
